import { useMemo, useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import TextField from "@/components/TextField";
import EventAssociationBanner from "../../../_components/EventAssociationBanner";
import { FormValues } from "../validation/schema";
import { useQuery } from "@apollo/client";
import { GET_ACTIVITY_TYPES } from "../../../queries";
import SectionAutocomplete from "../_components/SectionAutocomplete";
import { ActivityType } from "../../../types";
import { CreatesEventType } from "../../../types";
import Select from "@/components/Select";
import { isBeforeToday } from "@/lib/dateHelpers";

interface ActivityTypeOption {
  label: string;
  value: string;
  createsEvents: CreatesEventType;
}

interface ActivityDetailsStepProps {
  isActivityTypeDisabled?: boolean;
  totalParticipations?: number;
}

export default function ActivityDetailsStep({
  isActivityTypeDisabled = false,
  totalParticipations = 0
}: ActivityDetailsStepProps) {
  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext<FormValues>();

  const activityType = watch("activityType");
  const startDate = watch("startDate");
  const { data: activityTypesQuery } = useQuery(GET_ACTIVITY_TYPES);

  const activityTypeOptions: ActivityTypeOption[] = useMemo(
    () =>
      activityTypesQuery?.activityTypes?.edges?.map((edge: { node: ActivityType }) => {
        const activityType = edge.node ?? edge;

        return {
          label: activityType.title,
          value: activityType.id,
          createsEvents: activityType.createsEvent
        };
      }) || [],
    [activityTypesQuery]
  );

  const selectedActivityTypeData = activityTypeOptions?.find(
    (opt: ActivityTypeOption) => opt.value === activityType.id || opt.label === activityType.name
  );

  const showEventBanner =
    selectedActivityTypeData?.createsEvents === CreatesEventType.OPTIONAL &&
    totalParticipations === 0 &&
    !isBeforeToday(startDate?.startDate);

  useEffect(() => {
    if (selectedActivityTypeData) {
      setValue("activityType", {
        id: selectedActivityTypeData?.value ?? "",
        name: selectedActivityTypeData?.label ?? ""
      });
    }
  }, [selectedActivityTypeData, setValue]);
  return (
    <div className="flex flex-col gap-6">
      <p className="text-title-large">Enter details about the activity.</p>

      {showEventBanner && <EventAssociationBanner />}

      <Controller
        name="title"
        control={control}
        render={({ field: { onChange, value } }) => (
          <TextField
            className="w-full"
            label="Title"
            required
            value={value}
            onChange={onChange}
            error={!!errors.title}
            errorText={errors.title?.message as string}
          />
        )}
      />
      <Controller
        name="activityType"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            data-testid="event-type-select"
            label="Activity Type"
            placeholder="Select Activity Type"
            options={activityTypeOptions}
            className="w-full"
            value={value.id}
            disabled={isActivityTypeDisabled}
            onSelect={(value) => {
              const selectedEventType = activityTypeOptions?.find((opt: ActivityTypeOption) => opt.value === value);
              onChange({ id: value, name: selectedEventType?.label });
            }}
            required
            error={!!errors.activityType}
            errorText={errors.activityType?.message as string}
          />
        )}
      />

      <SectionAutocomplete />
      <Controller
        name="activityDescription"
        control={control}
        render={({ field: { onChange, value } }) => (
          <TextField
            className="w-full"
            label="Activity Description"
            type="textarea"
            value={value}
            onChange={onChange}
            error={!!errors.activityDescription}
            errorText={errors.activityDescription?.message as string}
          />
        )}
      />
    </div>
  );
}
